import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DownloadIcon,
  RefreshCw,
  MessageSquareIcon,
  UsersIcon,
  BarChartIcon,
} from 'lucide-react';
import { tenantApi, MessageTextMetrics, MessageSource, PopularMessage } from '../lib/tenant-api';
import { PageLoader } from '../components/common';
import DateRangePicker from '../components/DateRangePicker';

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  trend?: string;
  trendType?: 'positive' | 'negative';
}> = ({ icon, label, value, trend, trendType }) => {
  return (
    <div className="bg-white border border-gray-200  p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center text-gray-500 text-sm mb-2">
            <span className="mr-2">{icon}</span>
            <span>{label}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{value}</div>
          {trend && (
            <div className={`text-sm mt-2 font-medium ${trendType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
              {trend}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MessageSourceTable: React.FC<{
  data: MessageSource[];
}> = ({ data }) => {
  const [sortField, setSortField] = useState<keyof MessageSource>('total_messages');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    return 0;
  });

  const handleSort = (field: keyof MessageSource) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="bg-white border border-gray-200  overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Messages by Source</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => handleSort('source_display_name')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Source
              </th>
              <th
                onClick={() => handleSort('total_messages')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Total Messages
              </th>
              <th
                onClick={() => handleSort('unique_sessions')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Unique Users
              </th>
              <th
                onClick={() => handleSort('messages_per_session')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Avg. Messages/User
              </th>
              <th
                onClick={() => handleSort('messages_per_conversation')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Messages/Conversation
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length > 0 ? (
              sortedData.map((source, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {source.source_display_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {source.total_messages}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {source.unique_sessions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {source.messages_per_session.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {source.messages_per_conversation.toFixed(1)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                  No message data available for the selected period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PopularMessagesTable: React.FC<{
  data: PopularMessage[];
}> = ({ data }) => {
  return (
    <div className="bg-white border border-gray-200  overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Popular Messages</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Message Text
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Count
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unique Users
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((message, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                    <div className="line-clamp-2">{message.message_text}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {message.source_display_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {message.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {message.unique_sessions}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                  No popular messages found for the selected period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function MessageText() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [trafficType, setTrafficType] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<string>(localStorage.getItem('cta_date_range_selected') || 'today');


  const { data: metrics, isLoading, refetch } = useQuery<MessageTextMetrics>({
    queryKey: ['message-text-metrics', { date_from: dateFrom, date_to: dateTo, traffic_type: trafficType }],
    queryFn: () => tenantApi.getMessageTextMetrics({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      traffic_type: trafficType || undefined,
    }),
  });

  useEffect(() => {
    const persistedDateRange = localStorage.getItem('cta_date_range_type_selected');
    const persistedStartDate = localStorage.getItem('cta_date_range_start_date');
    const persistedEndDate = localStorage.getItem('cta_date_range_end_date');
    if (persistedDateRange && persistedStartDate && persistedEndDate) {
      const startDT = new Date(persistedStartDate);
      const endDT = new Date(persistedEndDate);
      handleDateRangeChange(persistedDateRange, startDT, endDT);
    }
  }, []);

  const handleDateRangeChange = (range: string, start?: Date, end?: Date) => {
    setSelectedDateRange(range);
    localStorage.setItem('cta_date_range_selected', range)
    if (start && end) {
      setDateFrom(`${start?.toISOString().split('T')[0]}`);
      setDateTo(`${end?.toISOString().split('T')[0]}`);
      refetch();
    }
  };

  const handleExport = () => {
    if (!metrics) return;

    const csvRows = [];

    // Add messages by source section
    if (metrics.by_source && metrics.by_source.length > 0) {
      csvRows.push(['Source', 'Total Messages', 'Unique Users', 'Avg Messages/User', 'Messages/Conversation'].join(','));

      metrics.by_source.forEach((source) => {
        csvRows.push([
          `"${source.source_display_name}"`,
          source.total_messages,
          source.unique_sessions,
          source.messages_per_session.toFixed(2),
          source.messages_per_conversation.toFixed(2)
        ].join(','));
      });
    }

    // Add popular messages section if present
    if (metrics.popular_messages && metrics.popular_messages.length > 0) {
      if (csvRows.length > 0) {
        csvRows.push(''); // Empty line separator
      }
      csvRows.push(['Message Text', 'Source', 'Count', 'Unique Users'].join(','));

      metrics.popular_messages.forEach((message) => {
        csvRows.push([
          `"${message.message_text.replace(/"/g, '""')}"`,
          `"${message.source_display_name}"`,
          message.count,
          message.unique_sessions
        ].join(','));
      });
    }

    // Check if we have any data to export
    if (csvRows.length === 0) {
      alert('No data available to export');
      return;
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `message-text-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/dashboard/engagement')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Engagement
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Conversation Interactions</h1>
          <p className="mt-1 text-sm text-gray-600">Popular question clicks and AMA chat messages</p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-gray-300  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <DownloadIcon className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200  p-4">
        <div className="flex items-center space-x-4">

          <DateRangePicker onDateRangeChange={handleDateRangeChange} selectedRange={selectedDateRange} />

          <div>
            <select
              value={trafficType}
              onChange={(e) => setTrafficType(e.target.value)}
              className="block w-full  border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Sources</option>
              <option value="website_organic">Website widget</option>
              <option value="landing_page">Lander Widget</option>
              <option value="website_sandbox">Sandbox</option>
            </select>
          </div>
          <div className="">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-gray-300  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          icon={<MessageSquareIcon className="h-5 w-5" />}
          label="Total Messages"
          value={metrics?.summary.total_messages || 0}
          trend="+1 vs last period"
          trendType="positive"
        />
        <MetricCard
          icon={<UsersIcon className="h-5 w-5" />}
          label="Total Users"
          value={metrics?.summary.unique_sessions || 0}
          trend="+8 vs last period"
          trendType="positive"
        />
        <MetricCard
          icon={<BarChartIcon className="h-5 w-5" />}
          label="Avg. Messages/User"
          value={
            metrics?.summary.unique_sessions
              ? (metrics.summary.total_messages / metrics.summary.unique_sessions).toFixed(1)
              : '0.0'
          }
          trend="+1.2 vs last period"
          trendType="positive"
        />
      </div>

      {/* Message Source Table */}
      <MessageSourceTable data={metrics?.by_source || []} />

      {/* Popular Messages Table */}
      <PopularMessagesTable data={metrics?.popular_messages || []} />
    </div>
  );
}
